import { EventEmitter, Injectable } from '@angular/core';
import {
    ISeSignResultData,
    SeArubaSignInfo,
    SeCompedSignInfo,
    SeInfoCertSignInfo,
    SeInsielARSSSignInfo,
    SeMultiSignInfo,
    SeSignInfo,
    SeSignRequestData,
    SeSissSignInfo,
    SeSissSignRequestData,
    SignPartialSuccessDetailedError,
    SignType,
    StoredSeSignInfo,
} from '../models/estensasign.model';
import { RadioSettingsService } from '../services/settings/radio-settings.service';
import { IsIntegrationWebService } from '../connectivity/is-integration-web.service';
import { TranslateService } from '@ngx-translate/core';
import { forEach } from 'lodash-es';
import { BehaviorSubject, first, firstValueFrom, map } from 'rxjs';
import { EstensaWaitingService } from '../services/waiting.service';
import { CancellationError } from '../confirm/confirm-dialog.service';
import { SissCloudService } from './siss-cloud/siss-cloud.service';
import { ModalDialogNotificationService, NotificationService, NotificationType } from '../services/notification.service';
import { Logger } from '@se/se.platform.base';
import { ButtonClick } from '../dialogs/se-modalwindow/se-modalwindow.component';
import { MatDialog } from '@angular/material/dialog';
import { CompedDoubleSignAuthDialogComponent } from './comped-double-sign-auth-dialog/comped-double-sign-auth-dialog.component';

type GetPdfCallback = (info: SeSignInfo) => Promise<ISeSignResultData[]>;
type SignCallback = (info: SeSignInfo) => Promise<ISeSignResultData[]>;
type MultiSignCallback = (info: SeMultiSignInfo) => Promise<any[]>; //@TODO 83782 replace any with proper return type
type CompletedCallback = (err?: Error) => void;

const defaultSignCallBack: SignCallback | GetPdfCallback = () => Promise.reject(new CancellationError());
const defaultMultiSignCallBack: MultiSignCallback = () => Promise.reject(new CancellationError());
const defaultCompletedCallback: CompletedCallback = () => null;

export interface NotifyPartialSuccessData {
    docsCount: string;
    unsignedDocsCount: string;
}

export interface NotifyPartialSuccessDetailedData extends NotifyPartialSuccessData {
    unsignedDocsDescriptions: string[];
}

@Injectable({
    providedIn: 'root',
})
export class EstensaSignService {
    // event
    openSignLoginWindowRequired: EventEmitter<SeSignRequestData> = new EventEmitter();
    openMultiSignLoginWindowRequired: EventEmitter<SeSignRequestData[]> = new EventEmitter();
    openMultiSignLoginWindowFromErrorRequired: EventEmitter<SeSignRequestData[]> = new EventEmitter();
    openSignLoginWindowFromErrorRequired: EventEmitter<SeSignRequestData> = new EventEmitter();
    notifySignSuccess: EventEmitter<NotifyPartialSuccessData | NotifyPartialSuccessDetailedData | void> =
        new EventEmitter();
    notifyMultiSignSuccess: EventEmitter<ISeSignResultData[] | void> = new EventEmitter();
    notifySignFailure: EventEmitter<string> = new EventEmitter();
    notifyMultiFailure: EventEmitter<ISeSignResultData[]> = new EventEmitter();

    // props
    private _currentSignType: SignType = SignType.Undefined;

    get currentSignType(): SignType {
        return this._currentSignType;
    }

    private _enableTimestamp: boolean = false;
    get enableTimestamp(): boolean {
        return this._enableTimestamp;
    }

    private _infocertSignTimeout: number = 0;

    get informCertSignTimeout(): number {
        return this._infocertSignTimeout;
    }

    signCallback: SignCallback = defaultSignCallBack;
    multiSignCallback: MultiSignCallback = defaultMultiSignCallBack;
    getPdfCallback: GetPdfCallback = defaultSignCallBack;
    completedCallback: CompletedCallback = defaultCompletedCallback;

    successMessageKey: string = '';
    errorMessageKey: string = '';

    reportErrorDetailList: any;

    /*@TODO gestire caso multi!!!*/
    storedSignerInfo: StoredSeSignInfo = {
        signerId: '',
        signerName: '',
        pin: '',
    };

    private timeoutFunc: any;
    timeout: number = 60;
    leaveSessionOpen: boolean = false;
    currentSignSessionId: string = '';

    private isAuthDialogOpen: boolean = false;
    private genericSignErrorMessage: string = '';

    public isWaiting = new BehaviorSubject<boolean>(false);

    // constructor
    constructor(
        private isIntegrationWebService: IsIntegrationWebService,
        private logger: Logger,
        private notificationService: NotificationService,
        private radioSettingService: RadioSettingsService,
        private sissCloudService: SissCloudService,
        private translateService: TranslateService,
        private waitingService: EstensaWaitingService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private dialogCtrl: MatDialog,
    ) {
        this.initService();
    }

    // public functions

    beginMultiSigningFlow(
        requestData: SeSignRequestData[],
        signCallback: MultiSignCallback,
        getPdfCallback: GetPdfCallback | null = null,
        completedCallback?: CompletedCallback,
        successMessageKey?: string,
        errorMessageKey?: string,
    ) {
        this.successMessageKey = successMessageKey ? successMessageKey : 'SuccessOperation';
        this.errorMessageKey = errorMessageKey ? errorMessageKey : 'Exception_SignFault';
        this.reportErrorDetailList = requestData[0].reportErrorDetailList; //@TODO fix
        this.genericSignErrorMessage = this.translateService.instant(this.errorMessageKey, { p0: '' });

        // Inizializzo le callback che mi passa il modulo che richiede la firma
        this.getPdfCallback = getPdfCallback ?? defaultSignCallBack;
        this.multiSignCallback = signCallback;
        this.completedCallback = completedCallback ?? defaultCompletedCallback;

        this.isAuthDialogOpen = this.openMultiAuthenticationWindowRequired(requestData);
        if (this.isAuthDialogOpen) {
            // Per sicurezza resetto le info salvate in precedenza
            this.clearStoredSignerInfo();
        }
        this.openMultiSignLoginWindowRequired.next(requestData);
    }

    async beginSigningFlow(
        requestData: SeSignRequestData,
        signCallback: SignCallback,
        getPdfCallback: GetPdfCallback | null = null,
        completedCallback?: CompletedCallback,
        successMessageKey?: string,
        errorMessageKey?: string,
    ) {
        this.successMessageKey = successMessageKey || 'SuccessOperation';
        this.errorMessageKey = errorMessageKey || 'Exception_SignFault';
        this.reportErrorDetailList = requestData.reportErrorDetailList;
        this.genericSignErrorMessage = this.translateService.instant(this.errorMessageKey, { p0: '' });

        // Inizializzo le callback che mi passa il modulo che richiede la firma
        this.getPdfCallback = getPdfCallback || defaultSignCallBack;
        this.signCallback = signCallback;
        this.completedCallback = completedCallback || defaultCompletedCallback;

        this.isAuthDialogOpen = this.shouldOpenAuthenticationWindow(requestData);
        if (this.isAuthDialogOpen) {
            // Per sicurezza resetto le info salvate in precedenza
            this.clearStoredSignerInfo();

            this.openSignLoginWindowRequired.next(requestData);
        } else {
            this.endSigningFlowWithoutAuthDialog(requestData);
        }
    }

    cancelSigningFlow() {
        this.completedCallback(new CancellationError());
    }

    async endMultiSignFlow(info: SeMultiSignInfo) {
        this.preMultiSignOps(info);
        try {
            const multiSignRes: ISeSignResultData[] = await this.multiSign(info);
            const hasErrors = multiSignRes.find((res) => res.serverError);
            if (hasErrors) this.notifyMultiFailure.next(multiSignRes);
            else this.notifyMultiSignSuccess.next(multiSignRes as any);
        } catch (err: any) {
            this.logger.error(err);
        } finally {
            this.postSignOps();
            this.completedCallback();
        }
    }

    endSigningFlow(info: SeSignInfo) {
        this.preSignOps(info);

        this.sign(info)
            .then((signedDocDataList: ISeSignResultData[]) => {
                switch (this.currentSignType) {
                    case SignType.SISSWay:
                    case SignType.SISS:
                    case SignType.CompedDigitalSign:
                        // Se è comped o siss ci puo' essere un errore rimbalzato dalle operazioni fatte negli step precedenti
                        let partiallySigned = 0;
                        for (let i = 0; i < signedDocDataList.length; i++) {
                            if (!signedDocDataList[i].signed) {
                                partiallySigned++;
                                this.notificationService.error(
                                    signedDocDataList[i].serverErrorDetail || 'N/A',
                                    this.translateService.instant(signedDocDataList[i].serverError || 'Error'),
                                );
                            }
                        }

                        if (partiallySigned <= 0) {
                            // OK
                            this.notifySignSuccess.next();
                            this.completedCallback();
                        } else if (partiallySigned < signedDocDataList.length) {
                            // FIRMA PARZIALE
                            return Promise.reject({
                                message: 'PARTIAL_' + partiallySigned + '_' + signedDocDataList.length,
                            });
                        } else if (partiallySigned >= signedDocDataList.length) {
                            // TUTTI NON FIRMATI
                            return Promise.reject({
                                message: this.translateService.instant('PartiallySignedDocOnTotal', {
                                    p0: partiallySigned,
                                    p1: signedDocDataList.length,
                                }),
                            });
                        }
                        break;
                    default:
                        this.notifySignSuccess.next();
                        this.completedCallback();
                        break;
                }

                this.postSignOps();
            })
            .catch((e) => {
                this.clearStoredSignerInfo();
                this.postSignOps();

                let msg = e && e.message ? e.message : this.genericSignErrorMessage;

                // Messaggio per tecnico.
                this.logger.warn(
                    "È possibile che alcuni referti siano stati presi in carico da altri. Riprovare l'operazione di firma.",
                );

                if (e instanceof SignPartialSuccessDetailedError) {
                    this.notifySignSuccess.next({
                        unsignedDocsCount: `${e.total - e.numSigned}`,
                        docsCount: `${e.total}`,
                        unsignedDocsDescriptions: e.details,
                    });
                } else if (msg.startsWith('PARTIAL_')) {
                    const data = msg.split('_');
                    this.notifySignSuccess.next({ unsignedDocsCount: data[1], docsCount: data[2] });
                    this.completedCallback();
                } else {
                    this.notifySignFailure.next(msg);
                    this.completedCallback(new Error(msg));
                }
            });
    }

    safeExit() {
        // Resetto le callback
        this.signCallback = defaultSignCallBack;
        this.getPdfCallback = defaultSignCallBack;
        this.completedCallback = defaultCompletedCallback;

        this.successMessageKey = 'SuccessOperation';
        this.errorMessageKey = 'Exception_SignFault';
    }

    // private functions

    /*
     * Esegue la firma utilizzando i dati passati come parametro e non quelli provenienti dalla finestra di autenticazione
     */
    private endSigningFlowWithoutAuthDialog(requestData: SeSignRequestData) {
        this.waitingService.isWaiting = true;

        const signInfo: SeSignInfo = new SeSignInfo();

        let msg: string = '';

        switch (this.currentSignType) {
            case SignType.SISSWay:
            case SignType.SISS:
                if (this.currentSignType == SignType.SISSWay) {
                    signInfo.sissWay = new SeSissSignInfo(requestData as SeSissSignRequestData);
                    signInfo.type = SignType.SISSWay;

                    signInfo.sissWay.signerName = requestData.signerName;
                    signInfo.sissWay.signerId = requestData.signerId;
                    signInfo.sissWay.reportIdList = requestData.reportIdList;
                    signInfo.sissWay.pin = '';

                    signInfo.sissWay.enableTimestamp = this.enableTimestamp;
                } else if (this.currentSignType == SignType.SISS) {
                    signInfo.siss = new SeSissSignInfo(requestData as SeSissSignRequestData);
                    signInfo.type = SignType.SISS;

                    signInfo.siss.signerName = requestData.signerName;
                    signInfo.siss.signerId = requestData.signerId;
                    signInfo.siss.reportIdList = requestData.reportIdList;
                    signInfo.siss.pin = '';

                    signInfo.siss.enableTimestamp = this.enableTimestamp;
                }

                this.sign(signInfo)
                    .then((signedDocDataList: ISeSignResultData[]) => {
                        this.waitingService.isWaiting = false;
                        let partiallySigned = 0;
                        for (let i = 0; i < signedDocDataList.length; i++) {
                            if (!signedDocDataList[i].signed) {
                                partiallySigned++;
                                this.notificationService.error(
                                    signedDocDataList[i].serverErrorDetail,
                                    this.translateService.instant(signedDocDataList[i].serverError || ''),
                                );
                            }
                        }

                        if (partiallySigned <= 0) {
                            // OK
                            this.notifySignSuccess.next();
                            this.completedCallback();
                        } else if (partiallySigned < signedDocDataList.length) {
                            // FIRMA PARZIALE
                            return Promise.reject({
                                message: 'PARTIAL_' + partiallySigned + '_' + signedDocDataList.length,
                            });
                        } else if (partiallySigned >= signedDocDataList.length) {
                            // TUTTI NON FIRMATI
                            return Promise.reject({
                                message: this.translateService.instant('PartiallySignedDocOnTotal', {
                                    p0: partiallySigned,
                                    p1: signedDocDataList.length,
                                }),
                            });
                        }
                    })
                    .catch((e) => {
                        this.waitingService.isWaiting = false;

                        this.clearStoredSignerInfo();

                        msg = e && e.message ? e.message : this.genericSignErrorMessage;

                        if (msg.startsWith('PARTIAL_')) {
                            const data = msg.split('_');
                            this.notifySignSuccess.next({ unsignedDocsCount: data[1], docsCount: data[2] });
                            this.completedCallback();
                        } else {
                            this.notifySignFailure.next(msg);
                            this.completedCallback(new Error(msg));
                        }
                    });

                break;
            case SignType.CompedDigitalSign:
                signInfo.comped = new SeCompedSignInfo(requestData);
                signInfo.comped.signerName = this.storedSignerInfo.signerName;
                signInfo.comped.signerId = this.storedSignerInfo.signerId;
                signInfo.comped.pin = this.storedSignerInfo.pin;
                signInfo.type = this.currentSignType;

                this.sign(signInfo)
                    .then((signedDocDataList: ISeSignResultData[]) => {
                        this.waitingService.isWaiting = false;

                        let partiallySigned = 0;
                        for (let i = 0; i < signedDocDataList.length; i++) {
                            if (!signedDocDataList[i].signed) {
                                partiallySigned++;
                                this.notificationService.error(
                                    signedDocDataList[i].serverErrorDetail,
                                    this.translateService.instant(signedDocDataList[i].serverError || ''),
                                );
                            }
                        }

                        if (partiallySigned <= 0) {
                            // OK
                            this.notifySignSuccess.next();
                            this.completedCallback();
                        } else if (partiallySigned < signedDocDataList.length) {
                            // FIRMA PARZIALE
                            return Promise.reject({
                                message: 'PARTIAL_' + partiallySigned + '_' + signedDocDataList.length,
                            });
                        } else if (partiallySigned >= signedDocDataList.length) {
                            // TUTTI NON FIRMATI
                            return Promise.reject({
                                message: this.translateService.instant('PartiallySignedDocOnTotal', {
                                    p0: partiallySigned,
                                    p1: signedDocDataList.length,
                                }),
                            });
                        }
                    })
                    .catch((e) => {
                        this.waitingService.isWaiting = false;

                        this.clearStoredSignerInfo();

                        msg = e && e.message ? e.message : this.genericSignErrorMessage;
                        this.notifySignFailure.next(msg);

                        // Se sto facendo una firma senza finestra di credenziali aperte notifico l'errore
                        // e riapro la finestra di autenticazione in stato di errore
                        if (!this.isAuthDialogOpen) {
                            signInfo.comped = new SeCompedSignInfo(requestData);
                            requestData.initialErrorMessage = msg;
                            this.beginSigningFlowFromError(requestData);
                        }
                    });

                break;
            case SignType.ARUBA:
                signInfo.aruba = new SeArubaSignInfo(requestData);
                signInfo.aruba.signerName = this.storedSignerInfo.signerName;
                signInfo.aruba.signerId = this.storedSignerInfo.signerId;
                signInfo.aruba.pin = this.storedSignerInfo.pin;
                signInfo.aruba.openedSessionId = this.currentSignSessionId; // inserisco il sessionId se è valorizzato (perchè deve usare quello)
                signInfo.type = this.currentSignType;

                this.sign(signInfo)
                    .then(() => {
                        this.waitingService.isWaiting = false;
                        this.notifySignSuccess.next();
                        this.completedCallback();
                    })
                    .catch((e) => {
                        this.waitingService.isWaiting = false;

                        this.clearStoredSignerInfo();
                        this.currentSignSessionId = '';

                        if (!!e && e.message == 'SESSION_KO') {
                            signInfo.aruba = new SeArubaSignInfo(requestData);
                            this.beginSigningFlow(requestData, this.signCallback);
                        } else {
                            msg = e && e.message ? e.message : this.genericSignErrorMessage;
                            this.notifySignFailure.next(msg);

                            // Se sto facendo una firma senza finestra di credenziali aperte notifico l'errore
                            // e riapro la finestra di autenticazione in stato di errore
                            if (!this.isAuthDialogOpen) {
                                signInfo.aruba = new SeArubaSignInfo(requestData);
                                requestData.initialErrorMessage = msg;
                                this.beginSigningFlowFromError(requestData);
                            }
                        }
                    });

                break;
            case SignType.InfoCert:
                signInfo.infoCert = new SeInfoCertSignInfo(requestData);
                signInfo.infoCert.signerName = this.storedSignerInfo.signerName;
                signInfo.infoCert.signerId = this.storedSignerInfo.signerId;
                signInfo.infoCert.pin = this.storedSignerInfo.pin;
                signInfo.type = this.currentSignType;

                this.sign(signInfo)
                    .then(() => {
                        this.waitingService.isWaiting = false;
                        this.notifySignSuccess.next();
                        this.completedCallback();
                    })
                    .catch((e) => {
                        this.waitingService.isWaiting = false;

                        this.clearStoredSignerInfo();
                        this.currentSignSessionId = '';

                        msg = e && e.message ? e.message : this.genericSignErrorMessage;

                        if (msg.startsWith('PARTIAL_')) {
                            const data = msg.split('_');
                            this.notifySignSuccess.next({ unsignedDocsCount: data[1], docsCount: data[2] });
                        } else {
                            this.notifySignFailure.next(msg);

                            // Se sto facendo una firma senza finestra di credenziali aperte notifico l'errore
                            // e riapro la finestra di autenticazione in stato di errore
                            if (!this.isAuthDialogOpen) {
                                signInfo.infoCert = new SeInfoCertSignInfo(requestData);
                                requestData.initialErrorMessage = msg;
                                this.beginSigningFlowFromError(requestData);
                            }
                        }
                    });

                break;
            case SignType.InsielARSS:
                signInfo.insielARSS = new SeInsielARSSSignInfo(requestData);
                signInfo.insielARSS.signerName = this.storedSignerInfo.signerName;
                signInfo.insielARSS.signerId = this.storedSignerInfo.signerId;
                signInfo.insielARSS.pin = ''; // this.storedSignerInfo.pin;
                signInfo.insielARSS.openedSessionId = this.currentSignSessionId; // inserisco il sessionId se è valorizzato (perchè deve usare quello)
                signInfo.type = this.currentSignType;

                this.sign(signInfo)
                    .then(() => {
                        this.waitingService.isWaiting = false;
                        this.notifySignSuccess.next();
                        this.completedCallback();
                    })
                    .catch((e) => {
                        this.waitingService.isWaiting = false;

                        this.clearStoredSignerInfo();
                        this.currentSignSessionId = '';

                        if (!!e && e.message == 'SESSION_KO') {
                            signInfo.insielARSS = new SeInsielARSSSignInfo(requestData);
                            this.beginSigningFlow(requestData, this.signCallback);
                        } else {
                            msg = e && e.message ? e.message : this.genericSignErrorMessage;
                            this.notifySignFailure.next(msg);

                            // Se sto facendo una firma senza finestra di credenziali aperte notifico l'errore
                            // e riapro la finestra di autenticazione in stato di errore
                            if (!this.isAuthDialogOpen) {
                                signInfo.insielARSS = new SeInsielARSSSignInfo(requestData);
                                requestData.initialErrorMessage = msg;
                                this.beginSigningFlowFromError(requestData);
                            }
                        }
                    });

                break;
            default:
                this.waitingService.isWaiting = false;
                msg = this.translateService.instant('DigitalSign_SignNotSupported');
                this.notifySignFailure.next(msg);
        }
    }

    private initService() {
        this.radioSettingService.radioSettingsAsync.subscribe((settings) => {
            this._currentSignType = settings.reportSettings.signType;
            this._enableTimestamp = settings.reportSettings.enableTimestamp;
            this._infocertSignTimeout = settings.reportSettings.infocertSignTimeout;
            if (this._currentSignType == SignType.InfoCert) {
                this.timeout = settings.reportSettings.infocertSignTimeout;
            }
        });
    }

    private beginSigningFlowFromError(data: SeSignRequestData) {
        this.openSignLoginWindowFromErrorRequired.next(data);
    }

    private preSignOps(info: SeSignInfo) {
        this.isWaiting.next(true);

        const msTimeout = /* Timeout espresso in minuti, ogni minuto ha (60 * 1000)ms  */ this.timeout * 60 * 1000;

        switch (this.currentSignType) {
            case SignType.SISSWay:
            case SignType.SISS:
                /* Operazioni preliminari Siss o SissWay*/
                break;
            case SignType.CompedDigitalSign:
                // Se arriva qui il timeout è > 0 - salvo le info e lancio la callback
                this.storedSignerInfo = {
                    signerName: info.comped.signerName,
                    signerId: info.comped.signerId,
                    pin: info.comped.pin,
                };

                // Puliscso se per caso esiste un timeout attivo
                if (this.timeoutFunc) {
                    clearTimeout(this.timeoutFunc);
                }

                // Lancio nuovo timeout
                this.timeoutFunc = setTimeout(() => {
                    this.clearStoredSignerInfo();
                }, msTimeout);

                break;
            case SignType.ARUBA:
                if (this.leaveSessionOpen) {
                    this.storedSignerInfo = {
                        signerName: info.aruba.signerName,
                        signerId: info.aruba.signerId,
                        pin: info.aruba.pin,
                    };
                } else {
                    this.currentSignSessionId = '';
                    this.storedSignerInfo = {
                        signerId: '',
                        signerName: '',
                        pin: '',
                    };
                }

                break;
            case SignType.InfoCert:
                // Se arriva qui il timeout è > 0 - salvo le info e lancio la callback
                this.storedSignerInfo = {
                    signerName: info.infoCert.signerName,
                    signerId: info.infoCert.signerId,
                    pin: info.infoCert.pin,
                };

                // Puliscso se per caso esiste un timeout attivo
                if (this.timeoutFunc) clearTimeout(this.timeoutFunc);

                // Lancio nuovo timeout
                this.timeoutFunc = setTimeout(() => {
                    this.clearStoredSignerInfo();
                }, msTimeout);

                break;
            case SignType.InsielARSS:
                if (this.leaveSessionOpen) {
                    this.storedSignerInfo = {
                        signerName: info.insielARSS.signerName,
                        signerId: info.insielARSS.signerId,
                        pin: '',
                    };
                } else {
                    this.currentSignSessionId = '';
                    this.storedSignerInfo = {
                        signerId: '',
                        signerName: '',
                        pin: '',
                    };
                }

                break;
            case SignType.Confirmo:
            case SignType.ScrybaSign:
                /* Operazioni preliminari */
                break;

            default:
            // do nothing
        }
    }

    /*@TODO verificare se non bisogna replicare le operazioni del sign singolo*/
    private preMultiSignOps(_: SeMultiSignInfo) {
        this.isWaiting.next(true);
    }

    private postSignOps() {
        this.isWaiting.next(false);
        this.leaveSessionOpen = false;
    }

    private openMultiAuthenticationWindowRequired = (data: SeSignRequestData[]): boolean => {
        switch (this.currentSignType) {
            case SignType.SISSWay:
            case SignType.SISS:
                return false; // L'autenticazione se la fa il siss o sissway
            case SignType.CompedDigitalSign:
            case SignType.InfoCert:
                // Apro la finestra di autenticazione se non ci sono info salvate, se l'id del firmatario salvato è diverso da quello che sta richiedendo di firmare
                // oppure se il timeout è impostato per richiedere sempre le credenziali
                return (
                    !this.storedSignerInfo ||
                    !this.storedSignerInfo.signerId ||
                    !this.storedSignerInfo.pin ||
                    this.storedSignerInfo.signerId != data[0].signerId ||
                    this.timeout <= 0
                );

            case SignType.ARUBA:
                // Apro la finestra di autenticazione se non ci sono info salvate (id e pin),
                // se l'id del firmatario salvato è diverso da quello che sta richiedendo di firmare
                // oppure se non esiste una sessione di firma
                return (
                    !this.storedSignerInfo ||
                    !this.storedSignerInfo.signerId ||
                    !this.storedSignerInfo.pin ||
                    this.storedSignerInfo.signerId != data[0].signerId ||
                    !this.currentSignSessionId
                );

            case SignType.InsielARSS:
                // Apro la finestra di autenticazione se non ci sono info salvate (id ),
                // se l'id del firmatario salvato è diverso da quello che sta richiedendo di firmare
                // oppure se non esiste una sessione di firma
                return (
                    !this.storedSignerInfo ||
                    !this.storedSignerInfo.signerId ||
                    this.storedSignerInfo.signerId != data[0].signerId ||
                    !this.currentSignSessionId
                );

            default:
                return true;
        }
    };

    private shouldOpenAuthenticationWindow(data: SeSignRequestData): boolean {
        switch (this.currentSignType) {
            case SignType.SISSWay:
            case SignType.SISS:
                return false; // L'autenticazione se la fa il siss o sissway
            case SignType.CompedDigitalSign:
            case SignType.InfoCert:
                // Apro la finestra di autenticazione se non ci sono info salvate, se l'id del firmatario salvato è diverso da quello che sta richiedendo di firmare
                // oppure se il timeout è impostato per richiedere sempre le credenziali
                return (
                    !this.storedSignerInfo ||
                    !this.storedSignerInfo.signerId ||
                    !this.storedSignerInfo.pin ||
                    this.storedSignerInfo.signerId != data.signerId ||
                    this.timeout <= 0
                );

            case SignType.ARUBA:
                // Apro la finestra di autenticazione se non ci sono info salvate (id e pin),
                // se l'id del firmatario salvato è diverso da quello che sta richiedendo di firmare
                // oppure se non esiste una sessione di firma
                return (
                    !this.storedSignerInfo ||
                    !this.storedSignerInfo.signerId ||
                    !this.storedSignerInfo.pin ||
                    this.storedSignerInfo.signerId != data.signerId ||
                    !this.currentSignSessionId
                );

            case SignType.InsielARSS:
                // Apro la finestra di autenticazione se non ci sono info salvate (id),
                // se l'id del firmatario salvato è diverso da quello che sta richiedendo di firmare
                // oppure se non esiste una sessione di firma
                return (
                    !this.storedSignerInfo ||
                    !this.storedSignerInfo.signerId ||
                    this.storedSignerInfo.signerId != data.signerId ||
                    !this.currentSignSessionId
                );

            default: // ScrybaSign e Confirmo aprono sempre
                return true;
        }
    }

    private clearStoredSignerInfo = () => {
        this.storedSignerInfo = {
            signerId: '',
            signerName: '',
            pin: '',
        };

        if (this.timeoutFunc) {
            clearTimeout(this.timeoutFunc);
            this.timeoutFunc = undefined;
        }
    };

    private async multiSign(info: SeMultiSignInfo): Promise<ISeSignResultData[]> {
        try {
            switch (this.currentSignType) {
                case SignType.InfoCert:
                case SignType.ARUBA:
                case SignType.Confirmo:
                    return await this.multiSignCallback(info);
                default:
                    return Promise.reject({
                        message: `${SignType[this.currentSignType]} ${this.translateService.instant(
                            'DigitalSign_SignNotSupported',
                        )}`,
                    });
            }
        } catch (signError: any) {
            return signError;
        }
    }

    doubleSignComped(requestData: SeSignRequestData[],
        signCallback: SignCallback,
        getPdfCallback: GetPdfCallback) {

        this.signCallback = signCallback;
        this.getPdfCallback = getPdfCallback;
        this.successMessageKey = 'SuccessOperation';
        this.errorMessageKey =  'Exception_SignFault';
        this.reportErrorDetailList = requestData[0].reportErrorDetailList;
        this.genericSignErrorMessage = this.translateService.instant(this.errorMessageKey, { p0: '' });

        let signInfo1: SeSignInfo = new SeSignInfo();
        signInfo1.comped = new SeCompedSignInfo(requestData[0]);
        let signInfo2: SeSignInfo = new SeSignInfo();
        signInfo2.comped = new SeCompedSignInfo(requestData[1]);

        const firstSigner = this.translateService.instant('ReferringPhysician') + ": " + signInfo1.comped.signerName;
        const secondSigner = this.translateService.instant('SecondReferringPhysician') + ": " + signInfo2.comped.signerName;

        this.waitingService.isWaiting = true;

        return Promise.all([this.getPdfCallback(signInfo1), this.getPdfCallback(signInfo2)])
            .then(async ([signingDocDataList1, signingDocDataList2]) => {
                this.waitingService.isWaiting = false;
                
                // Verifico il CDA
                this.handleCDAErrorOnTotal(signingDocDataList1);
                // Gestione errori significantImages
                this.handleSigificantImagesErrors(signingDocDataList1);

                if (!(signingDocDataList1.length == 1 && signingDocDataList1[0].srcDocByteArray && signingDocDataList1[0].signerCode)) {
                    const serverErrorDetail = signingDocDataList1.find(d => d.serverError)?.serverErrorDetail;
                    const serverError = signingDocDataList1.find(d => d.serverError)?.serverError;
                    return Promise.reject({
                        message: this.translateService.instant('DigitalSign_PdfDownloadFailure'),
                        title: serverError ? this.translateService.instant(serverError) : this.translateService.instant('Error'),
                        detail: serverErrorDetail ?? this.translateService.instant('DigitalSign_PdfDownloadFailure')
                    });
                }

                if (!(signingDocDataList2.length == 1 && signingDocDataList2[0].signerCode)) {
                    const serverErrorDetail = signingDocDataList2.find(d => d.serverError)?.serverErrorDetail;
                    const serverError = signingDocDataList2.find(d => d.serverError)?.serverError;

                    return Promise.reject({
                        message:  this.translateService.instant('DigitalSign_PdfDownloadFailure'),
                        title: serverError ? this.translateService.instant(serverError) : this.translateService.instant('Error'),
                        detail: serverErrorDetail ?? this.translateService.instant('DigitalSign_PdfDownloadFailure')
                    });
                }
                // Ora procedo con il primo firmatario
                const compedSign1 = await this.openCompedDoublesignDialog(signInfo1, true);
                if (compedSign1) {
                    let signerCode1 = signingDocDataList1[0].signerCode;

                    compedSign1.comped.signerCode = signerCode1;
                    compedSign1.comped.docDataArray = signingDocDataList1;
                    compedSign1.comped.enableTimestamp = this.enableTimestamp;
                    this.waitingService.isWaiting = true;
                    return this.signWithComped(compedSign1.comped).then(async compedFirstSignInfo => {
                        this.waitingService.isWaiting = false;
                        const isFirstSignOk = compedFirstSignInfo.comped.docDataArray[0].signed;
                        if (isFirstSignOk) {
                            signingDocDataList2[0].srcDocByteArray = compedFirstSignInfo.comped.docDataArray[0].signedDocByteArray;
                            // Ora procedo con il secondo firmatario
                            const compedSign2 = await this.openCompedDoublesignDialog(signInfo2);

                            if (compedSign2) {
                                let signerCode2 = signingDocDataList2[0].signerCode;

                                compedSign2.comped.signerCode = signerCode2;
                                compedSign2.comped.docDataArray = signingDocDataList2;
                                compedSign2.comped.enableTimestamp = this.enableTimestamp;
                                compedSign2.comped.isSecondSigner = true;

                                this.waitingService.isWaiting = true;
                                return this.signWithComped(compedSign2.comped).then(finalCompedSignInfo => {
                                    const isSecondSignOk = finalCompedSignInfo.comped.docDataArray[0].signed;
                                    if (isSecondSignOk) {
                                        // Chiamata finale  per store
                                        return this.signCallback(finalCompedSignInfo).then((finalSignedDoc) => {

                                            this.waitingService.isWaiting = false;
                                            if (finalSignedDoc[0].signed) {

                                                this.notificationService.success(this.translateService.instant('DigitallySigned'));
                                                this.modalDialogNotificationService.notify({
                                                    message: this.translateService.instant('SuccessOperation', { p0: '' }),
                                                    title: this.translateService.instant('Information'),
                                                    type: NotificationType.information,
                                                    buttonYes: false,
                                                    buttonNo: false,
                                                    buttonOk: true,
                                                    buttonCancel: false,
                                                });

                                                this.safeExit();
                                                return Promise.resolve();
                                            } else {
                                                const serverError = finalSignedDoc[0].serverError ? this.translateService.instant(finalSignedDoc[0].serverError) +
                                                    (finalSignedDoc[0].serverErrorDetail ? " -- " + finalSignedDoc[0].serverErrorDetail : "") : this.translateService.instant('Exception_SignFault', { p0: '' });

                                                return Promise.reject({
                                                    message: this.translateService.instant('Exception_SignFault', { p0: '' }),
                                                    title: this.translateService.instant('Error'),
                                                    detail: serverError 
                                                });
                                            }

                                        }).catch(err => {
                                            this.waitingService.isWaiting = false;
                                            return Promise.reject(err);
                                        });

                                    } else {
                                        const serverErrorDetail = finalCompedSignInfo.comped.docDataArray[0].serverErrorDetail;
                                        return Promise.reject({
                                            message: this.translateService.instant('Exception_SignFault', { p0: secondSigner }),
                                            title: this.translateService.instant('Error'),
                                            serverErrorDetail: serverErrorDetail ?? this.translateService.instant('Exception_SignFault', { p0: secondSigner })
                                        });
                                    }
                                }).catch(err => {
                                    this.waitingService.isWaiting = false;
                                    return Promise.reject({
                                        message: err && err.message ? err.message : this.translateService.instant('Exception_SignFault', { p0: secondSigner }),
                                        title: err && err.title ? err.title : this.translateService.instant('Error'),
                                        detail: err && err.detail ? err.detail : this.translateService.instant('Exception_SignFault', { p0: secondSigner })
                                    });
                                });
                            } else {
                                return Promise.reject(new CancellationError());
                            }

                        } else {
                            const serverErrorDetail = compedFirstSignInfo.comped.docDataArray[0].serverErrorDetail;
                            return Promise.reject({
                                message:  this.translateService.instant('Exception_SignFault', { p0: '' }),
                                title:  this.translateService.instant('Error'),
                                detail: serverErrorDetail ?? this.translateService.instant('Exception_SignFault', { p0: '' })
                            });
                        }

                    }).catch(err => {
                        this.waitingService.isWaiting = false;
                        if (err instanceof CancellationError) {
                            return Promise.reject(err);
                        }
                        return Promise.reject({
                            message: err && err.message ? err.message : this.translateService.instant('Exception_SignFault', { p0: firstSigner }),
                            title: err && err.title ? err.title : this.translateService.instant('Error'),
                            detail: err && err.detail ? err.detail : this.translateService.instant('Exception_SignFault', { p0: firstSigner })
                        });
                    });


                } else {
                    return Promise.reject(new CancellationError());
                }


            })
            .catch(err => {
                this.waitingService.isWaiting = false;

                let msg = err && err.message ? err.message : this.translateService.instant('Exception_SignFault', { p0: '' });
                let title = err && err.title ? err.title : this.translateService.instant('Error');
                let detail = err && err.detail ? this.translateService.instant(err.detail) : msg;

                if (err instanceof CancellationError) {
                    this.notificationService.error(
                        err.message,
                        this.translateService.instant('Error'),
                    );
                } else {

    
                    this.notificationService.error(
                        detail,
                        title,
                    );
                   
                    this.modalDialogNotificationService.notify({
                        message: msg,
                        title: this.translateService.instant('Error'),
                        type: NotificationType.error,
                        buttonYes: false,
                        buttonNo: false,
                        buttonOk: true,
                        buttonCancel: false,
                    });
                    
                }

                this.safeExit();
                return Promise.reject({ message: msg, title })

            });

    }
    private async openCompedDoublesignDialog(signer: SeSignInfo,skipInfoDialog = false): Promise<SeSignInfo | null> {
        return new Promise(async (resolve, reject) => {
            //const message = 'Inserire smart-card di ' + signer.comped.signerName + ' e premere OK';
            const message = this.translateService.instant('DigitalSign_SmartCard', { p0: signer.comped.signerName });
            const dialogResponse = skipInfoDialog ? true : await firstValueFrom(
                this.modalDialogNotificationService
                    .notify({
                        message,
                        title: this.translateService.instant('Warning'),
                        type: NotificationType.information,
                        buttonYes: false,
                        buttonNo: false,
                        buttonOk: true,
                        buttonCancel: true,
                    })
                    .pipe(
                        first(),
                        map((button) => {
                            if (button === ButtonClick.ok) return true;
                            return false;
                        }),
                    ),
            );
            if (dialogResponse) {

                const dialogRef = this.dialogCtrl.open(CompedDoubleSignAuthDialogComponent, {
                    data: signer,
                    autoFocus: true,
                    height: '35%',
                    width: '25%',
                    minWidth: '480px',
                    minHeight: '465px',
                    panelClass: 'outer-sign-dialog-wrapper',
                });
                const result: SeSignInfo | null = await firstValueFrom(dialogRef.afterClosed().pipe(first()));
                resolve(result);
                console.log("dati dialog", result);
            } else {
                resolve(null);
            }
        })

    }
    private async sign(info: SeSignInfo) {
        try {
            switch (this.currentSignType) {
                case SignType.SISSCloud:
                case SignType.SISSWayCloud:
                    return this.signCallback(info).then((signedDocDataList: ISeSignResultData[]) => {
                        // Verifico il CDA
                        this.handleCDAErrorOnTotal(signedDocDataList);
                        // Gestione errori significantImages
                        this.handleSigificantImagesErrors(signedDocDataList);

                        return this.sissCloudService.handleSignResult(signedDocDataList);
                    });
                case SignType.ARUBA:
                    info.aruba.openedSessionId = this.currentSignSessionId;

                    // Firma singola e multipla ( le info sono dentro la collection SignInfo.reportIdList )
                    return this.signCallback(info)
                        .then((signedDocDataList: ISeSignResultData[]) => {
                            // Verifico il CDA
                            this.handleCDAErrorOnTotal(signedDocDataList);
                            // Gestione errori significantImages
                            this.handleSigificantImagesErrors(signedDocDataList);
                            // Per prima cosa verifico se ci sono problemi con la sessione...
                            try {
                                if (
                                    !!signedDocDataList[0].serverErrorCode &&
                                    signedDocDataList[0].serverErrorCode == 'SESSION_KO'
                                ) {
                                    let errMsg = this.translateService.instant(signedDocDataList[0].serverError || '', {
                                        p0: '',
                                    });
                                    if (signedDocDataList[0].serverErrorDetail)
                                        errMsg += ': ' + signedDocDataList[0].serverErrorDetail;
                                    return Promise.reject({ message: errMsg });
                                } else if (
                                    !!signedDocDataList[0].serverErrorCode &&
                                    signedDocDataList[0].serverErrorCode == 'GENERIC_EXCEPTION'
                                )
                                    return Promise.reject({ message: signedDocDataList[0].serverErrorDetail });
                            } catch (e) {
                                return Promise.reject(e);
                            }

                            // Recupero l'id della sessione di firma  da usare in futuro.
                            if (this.leaveSessionOpen) {
                                try {
                                    this.currentSignSessionId = signedDocDataList[0].signSessionId || '';
                                } catch (e) {
                                    return Promise.reject(e);
                                }
                            }

                            // Verifico che tutti i documenti inviati alla firma siano stati firmati
                            let unsignedDocsCount = this.getUnsignedDocsCount(signedDocDataList);

                            // Tutti firmati
                            if (unsignedDocsCount <= 0) return Promise.resolve(signedDocDataList);

                            // Tutti non firmati
                            if (unsignedDocsCount == signedDocDataList.length)
                                return Promise.reject({
                                    message: this.translateService.instant('PartiallySignedDocOnTotal', {
                                        p0: unsignedDocsCount,
                                        p1: signedDocDataList.length,
                                    }),
                                });

                            return Promise.reject({
                                message: 'PARTIAL_' + unsignedDocsCount + '_' + signedDocDataList.length,
                            });
                        })
                        .catch((e) => {
                            // Se il messaggio è un genericSignErrorMessage non notifico (verrà fatto in seguito...)
                            if (!!e && !!e.message && e.message != this.genericSignErrorMessage)
                                return Promise.reject({ message: e.message });

                            return Promise.reject({ message: this.genericSignErrorMessage });
                        });
                case SignType.SISSWay:
                case SignType.SISS:
                case SignType.CompedDigitalSign:
                    // Richiedo il pdf da firmare, quindi chiamo la firma comped\sissway su ISIntegration
                    return this.getPdfCallback(info)
                        .then((signingDocDataList: ISeSignResultData[]) => {
                            // Verifico il CDA
                            this.handleCDAErrorOnTotal(signingDocDataList);
                            // Gestione errori significantImages
                            this.handleSigificantImagesErrors(signingDocDataList);

                            if (signingDocDataList.length > 0) {
                                // Verifico che tutti i documenti richiesti siano stati scaricati
                                let partiallyDownloaded = 0;
                                forEach(signingDocDataList, (data: ISeSignResultData) => {
                                    if (!!!data.srcDocByteArray) {
                                        partiallyDownloaded++;

                                        let reportInfo = this.reportErrorDetailList.find(
                                            (item: { id: string }) => item.id == data.reportId,
                                        );
                                        let reportDescr =
                                            reportInfo && reportInfo.message ? reportInfo.message : data.reportId;
                                        if (!data.serverError) data.serverError = this.errorMessageKey;

                                        let errMsg =
                                            this.translateService.instant('Report') +
                                            ': ' +
                                            reportDescr +
                                            ' - ' +
                                            this.translateService.instant(data.serverError, { p0: '' });
                                        if (data.serverErrorDetail) errMsg += ': ' + data.serverErrorDetail;

                                        // Notifico il mancato download del pdf
                                        this.notificationService.error(
                                            errMsg,
                                            this.translateService.instant('PartiallyDownloadDocOnTotal', {
                                                p0: partiallyDownloaded,
                                                p1: signingDocDataList.length,
                                            }),
                                        );
                                    }
                                });

                                // Tutti non scaricati => mi fermo
                                if (partiallyDownloaded == signingDocDataList.length)
                                    return Promise.reject({
                                        message: this.translateService.instant('PartiallyDownloadDocOnTotal', {
                                            p0: partiallyDownloaded,
                                            p1: signingDocDataList.length,
                                        }),
                                    });

                                // Salto eventuali doc array non scaricati per i quali signer code potrebbe essere nullo
                                let signerCode = signingDocDataList.find(
                                    (x) => !!x.srcDocByteArray && !!x.signerCode,
                                )?.signerCode;

                                if (this.currentSignType == SignType.CompedDigitalSign) {
                                    // Lancio la firma comped anche su un download parziale (l'errore sarà poi gestiti nei futuri step)
                                    info.comped.docDataArray = signingDocDataList;
                                    info.comped.signerCode = signerCode || '';
                                    info.comped.enableTimestamp = this.enableTimestamp;
                                    return this.signWithComped(info.comped);
                                } else if (this.currentSignType == SignType.SISSWay) {
                                    // Lancio la firma sissway anche su un download parziale (l'errore sarà poi gestiti nei futuri step)
                                    info.sissWay.docDataArray = signingDocDataList;
                                    info.sissWay.signerCode = signerCode || '';

                                    return this.signWithSISSWay(info.sissWay);
                                } else if (this.currentSignType == SignType.SISS) {
                                    // Lancio la firma sissway anche su un download parziale (l'errore sarà poi gestiti nei futuri step)
                                    info.siss.docDataArray = signingDocDataList;
                                    info.siss.signerCode = signerCode || '';

                                    return this.signWithSiss(info.siss);
                                }
                            }

                            return Promise.reject({
                                message: this.translateService.instant('CannotGeneratePdfReport'),
                            });
                        })
                        .then(this.signCallback)
                        .catch((e) => {
                            this.isIntegrationWebService.onIsIntegrationNotActive(null);
                            return Promise.reject(e);
                        });
                case SignType.InfoCert:
                    // Firma singola e multipla ( le info sono dentro la collection SignInfo.reportIdList )
                    return this.signCallback(info)
                        .then((signedDocDataList: ISeSignResultData[]) => {
                            // Verifico il CDA
                            this.handleCDAErrorOnTotal(signedDocDataList);
                            // Gestione errori significantImages
                            this.handleSigificantImagesErrors(signedDocDataList);

                            // Verifico che tutti i documenti inviati alla firma siano stati firmati
                            let unsignedDocsCount = this.getUnsignedDocsCount(signedDocDataList);

                            // Tutti firmati
                            if (unsignedDocsCount <= 0) return Promise.resolve(signedDocDataList);

                            // Tutti non firmati
                            if (unsignedDocsCount == signedDocDataList.length)
                                return Promise.reject({
                                    message: this.translateService.instant('PartiallySignedDocOnTotal', {
                                        p0: unsignedDocsCount,
                                        p1: signedDocDataList.length,
                                    }),
                                });

                            return Promise.reject({
                                message: 'PARTIAL_' + unsignedDocsCount + '_' + signedDocDataList.length,
                            });
                        })
                        .catch((e) => {
                            // Se il messaggio è un genericSignErrorMessage non notifico (verrà fatto in seguito...)
                            if (!!e && !!e.message && e.message != this.genericSignErrorMessage)
                                return Promise.reject({ message: e.message });

                            return Promise.reject({ message: this.genericSignErrorMessage });
                        });
                case SignType.Confirmo:
                    // Firma singola e multipla ( le info sono dentro la collection SignInfo.reportIdList )
                    return this.signCallback(info)
                        .then((signedDocDataList: ISeSignResultData[]) => {
                            // Verifico il CDA
                            this.handleCDAErrorOnTotal(signedDocDataList);
                            // Gestione errori significantImages
                            this.handleSigificantImagesErrors(signedDocDataList);

                            // Verifico che tutti i documenti inviati alla firma siano stati firmati
                            let unsignedDocsCount = this.getUnsignedDocsCount(signedDocDataList);

                            // Tutti firmati
                            if (unsignedDocsCount <= 0) return Promise.resolve(signedDocDataList);

                            // Tutti non firmati
                            if (unsignedDocsCount == signedDocDataList.length)
                                return Promise.reject({
                                    message: this.translateService.instant('PartiallySignedDocOnTotal', {
                                        p0: unsignedDocsCount,
                                        p1: signedDocDataList.length,
                                    }),
                                });

                            return Promise.reject({
                                message: 'PARTIAL_' + unsignedDocsCount + '_' + signedDocDataList.length,
                            });
                        })
                        .catch((e) => {
                            // Se il messaggio è un genericSignErrorMessage non notifico (verrà fatto in seguito...)
                            if (!!e && !!e.message && e.message != this.genericSignErrorMessage)
                                return Promise.reject({ message: e.message });

                            return Promise.reject({ message: this.genericSignErrorMessage });
                        });
                case SignType.InsielARSS:
                    info.insielARSS.openedSessionId = this.currentSignSessionId;

                    // Firma singola e multipla ( le info sono dentro la collection SignInfo.reportIdList )
                    return this.signCallback(info)
                        .then((signedDocDataList: ISeSignResultData[]) => {
                            // Verifico il CDA
                            this.handleCDAErrorOnTotal(signedDocDataList);
                            // Gestione errori significantImages
                            this.handleSigificantImagesErrors(signedDocDataList);

                            // Per prima cosa verifico se ci sono problemi con la sessione...
                            try {
                                if (
                                    !!signedDocDataList[0].serverErrorCode &&
                                    signedDocDataList[0].serverErrorCode == 'SESSION_KO'
                                ) {
                                    let errMsg = this.translateService.instant(signedDocDataList[0].serverError || '', {
                                        p0: '',
                                    });
                                    if (signedDocDataList[0].serverErrorDetail)
                                        errMsg += ': ' + signedDocDataList[0].serverErrorDetail;
                                    return Promise.reject({ message: errMsg });
                                } else if (
                                    !!signedDocDataList[0].serverErrorCode &&
                                    signedDocDataList[0].serverErrorCode == 'GENERIC_EXCEPTION'
                                )
                                    return Promise.reject({ message: signedDocDataList[0].serverErrorDetail });
                            } catch (e) {
                                return Promise.reject(e);
                            }

                            // Recupero l'id della sessione di firma  da usare in futuro.
                            if (this.leaveSessionOpen) {
                                try {
                                    this.currentSignSessionId = signedDocDataList[0].signSessionId || '';
                                } catch (e) {
                                    return Promise.reject(e);
                                }
                            }

                            // Verifico che tutti i documenti inviati alla firma siano stati firmati
                            let unsignedDocsCount = this.getUnsignedDocsCount(signedDocDataList);

                            // Tutti firmati
                            if (unsignedDocsCount <= 0) return Promise.resolve(signedDocDataList);

                            // Tutti non firmati
                            if (unsignedDocsCount == signedDocDataList.length)
                                return Promise.reject({
                                    message: this.translateService.instant('PartiallySignedDocOnTotal', {
                                        p0: unsignedDocsCount,
                                        p1: signedDocDataList.length,
                                    }),
                                });

                            return Promise.reject({
                                message: 'PARTIAL_' + unsignedDocsCount + '_' + signedDocDataList.length,
                            });
                        })
                        .catch((e) => {
                            // Se il messaggio è un genericSignErrorMessage non notifico (verrà fatto in seguito...)
                            if (!!e && !!e.message && e.message != this.genericSignErrorMessage)
                                return Promise.reject({ message: e.message });

                            return Promise.reject({ message: this.genericSignErrorMessage });
                        });
                case SignType.ScrybaSign:
                    // Firma singola e multipla ( le info sono dentro la collection SignInfo.reportIdList )
                    return this.signCallback(info)
                        .then((signedDocDataList: ISeSignResultData[]) => {
                            // Verifico il CDA
                            this.handleCDAErrorOnTotal(signedDocDataList);
                            // Gestione errori significantImages
                            this.handleSigificantImagesErrors(signedDocDataList);

                            let errorCode: string = signedDocDataList[0].serverErrorCode;

                            // Per prima cosa verifico se ci sono problemi con la sessione...
                            if (!!errorCode) {
                                try {
                                    if (errorCode == 'SESSION_KO') {
                                        let errMsg = this.translateService.instant(
                                            signedDocDataList[0].serverError || '',
                                            { p0: '' },
                                        );

                                        if (signedDocDataList[0].serverErrorDetail)
                                            errMsg += ': ' + signedDocDataList[0].serverErrorDetail;

                                        return Promise.reject({ message: errMsg });
                                    } else if (
                                        errorCode == 'GENERIC_EXCEPTION' ||
                                        errorCode == '1002' ||
                                        errorCode == '1003'
                                    )
                                        return Promise.reject({ message: signedDocDataList[0].serverErrorDetail });
                                } catch (e) {
                                    return Promise.reject(e);
                                }
                            }

                            // Recupero l'id della sessione di firma  da usare in futuro.
                            if (this.leaveSessionOpen) {
                                try {
                                    this.currentSignSessionId = signedDocDataList[0].signSessionId || '';
                                } catch (e) {
                                    return Promise.reject(e);
                                }
                            }

                            // Verifico che tutti i documenti inviati alla firma siano stati firmati
                            let unsignedDocsCount = this.getUnsignedDocsCount(signedDocDataList);

                            // Tutti firmati
                            if (unsignedDocsCount <= 0) return Promise.resolve(signedDocDataList);

                            // Tutti non firmati
                            if (unsignedDocsCount == signedDocDataList.length)
                                return Promise.reject({
                                    message: this.translateService.instant('PartiallySignedDocOnTotal', {
                                        p0: unsignedDocsCount,
                                        p1: signedDocDataList.length,
                                    }),
                                });

                            return Promise.reject({
                                message: 'PARTIAL_' + unsignedDocsCount + '_' + signedDocDataList.length,
                            });
                        })
                        .catch((e) => {
                            // Se il messaggio è un genericSignErrorMessage non notifico (verrà fatto in seguito...)
                            if (!!e && !!e.message && e.message != this.genericSignErrorMessage)
                                return Promise.reject({ message: e.message });

                            return Promise.reject({ message: this.genericSignErrorMessage });
                        });
                default:
                    return Promise.reject({ message: this.translateService.instant('DigitalSign_SignNotSupported') });
            }
        } catch (e) {
            return Promise.reject(e);
        }
    }

    private getUnsignedDocsCount(signedDocDataList: ISeSignResultData[]): number {
        let unsigned: number = 0;

        forEach(signedDocDataList, (data) => {
            if (!data.signed) {
                unsigned++;

                let reportInfo = this.reportErrorDetailList.find((item: { id: string }) => item.id == data.reportId);
                let reportDescr = reportInfo && reportInfo.message ? reportInfo.message : data.reportId;

                let errMsg =
                    this.translateService.instant('Report') +
                    ': ' +
                    reportDescr +
                    ' - ' +
                    this.translateService.instant(data.serverError || '', { p0: '' });

                if (data.serverErrorDetail) errMsg += ': ' + data.serverErrorDetail;

                this.notificationService.error(
                    errMsg,
                    this.translateService.instant('PartiallySignedDocOnTotal', {
                        p0: unsigned,
                        p1: signedDocDataList.length,
                    }),
                );
            }
        });

        return unsigned;
    }

    private signWithSiss(sissInfo: SeSissSignInfo): Promise<SeSignInfo> {
        return this.isIntegrationWebService
            .sissSign(sissInfo)
            .then((signedDocDataList: ISeSignResultData[]) => {
                this.isIntegrationWebService.onIsIntegrationActive();

                // Controllo che i documenti siano firmati - notifico quelli falliti - archivio i bytearray
                let partiallySigned = 0;
                forEach(signedDocDataList, (data: ISeSignResultData) => {
                    if (data.signed) {
                        // Marco come firmato, aggiungo il bytearray del p7m e rimuovo il bytearray del pdf
                        for (let n = 0; n < sissInfo.docDataArray.length; n++) {
                            if (sissInfo.docDataArray[n].reportId == data.reportId) {
                                sissInfo.docDataArray[n].signed = true;
                                sissInfo.docDataArray[n].signedDocByteArray = data.signedDocByteArray; // Mappo i p7m di ritorno
                                sissInfo.docDataArray[n].srcDocByteArray = undefined; // rimuovo il byte array del doc da firmare
                                sissInfo.docDataArray[n].publishInfo = data.publishInfo;
                                sissInfo.docDataArray[n].signedDAOByteArray = data.signedDAOByteArray;
                                sissInfo.docDataArray[n].identificativoDocumento = data.identificativoDocumento;
                                sissInfo.docDataArray[n].identificativoDAO = data.identificativoDAO;
                                break;
                            }
                        }
                    } else {
                        partiallySigned++;
                        // Marco come NON firmato e rimuovo il bytearray del pdf
                        for (let n = 0; n < sissInfo.docDataArray.length; n++) {
                            if (sissInfo.docDataArray[n].reportId == data.reportId) {
                                sissInfo.docDataArray[n].signed = false;
                                sissInfo.docDataArray[n].signedDocByteArray = undefined;
                                sissInfo.docDataArray[n].srcDocByteArray = undefined;
                                sissInfo.docDataArray[n].serverError =
                                    data.serverError !== null && data.serverError !== ''
                                        ? this.translateService.instant(data.serverError || '')
                                        : this.translateService.instant('SignReportError');
                                sissInfo.docDataArray[n].serverErrorCode =
                                    data.serverErrorCode !== null && data.serverErrorCode !== ''
                                        ? this.translateService.instant(data.serverErrorCode || '')
                                        : '';
                                sissInfo.docDataArray[n].serverErrorDetail =
                                    data.serverErrorDetail !== null && data.serverErrorDetail !== ''
                                        ? this.translateService.instant(data.serverErrorDetail || '')
                                        : this.translateService.instant('Exception_GenericFault');
                                break;
                            }
                        }

                        // Notifica errore
                        let reportInfo = this.reportErrorDetailList.find((item) => item.id == data.reportId);
                        let reportDescr = reportInfo && reportInfo.message ? reportInfo.message : data.reportId;

                        if (!data.serverError) data.serverError = this.errorMessageKey;

                        let errMsg =
                            this.translateService.instant('Report') +
                            ': ' +
                            reportDescr +
                            ' - ' +
                            this.translateService.instant(data.serverError, { p0: '' });
                        errMsg += data.serverErrorDetail ? ': ' + data.serverErrorDetail : '';

                        this.notificationService.error(
                            errMsg,
                            this.translateService.instant('PartiallySignedDoc', {
                                p0: partiallySigned,
                                p1: signedDocDataList.length,
                            }),
                        );
                    }
                });

                // Tutti non firmati o firmati parzialmente mi fermo - Da gestire il caso parziale di firma
                if (partiallySigned == signedDocDataList.length)
                    return Promise.reject({
                        message: this.translateService.instant('PartiallySignedDocOnTotal', {
                            p0: partiallySigned,
                            p1: signedDocDataList.length,
                        }),
                    });

                // Tutti o parzialmente firmati vado avati lo stesso
                const info: SeSignInfo = new SeSignInfo();
                info.type = SignType.SISS;
                info.siss = sissInfo;

                return Promise.resolve(info);
            })
            .catch((e) => {
                this.isIntegrationWebService.onIsIntegrationNotActive(null);

                if (!!e && e.status == -1)
                    return Promise.reject({ message: this.translateService.instant('IsIntegrationDisabled') });

                // Se il messaggio è un genericSignErrorMessage non notifico (verrà fatto in seguito...)
                if (!!e && !!e.message && e.message != this.genericSignErrorMessage)
                    return Promise.reject({ message: e.message });

                return Promise.reject({ message: this.genericSignErrorMessage });
            });
    }

    private signWithSISSWay(sissWayInfo: SeSissSignInfo): Promise<SeSignInfo> {
        return this.isIntegrationWebService
            .sisswaySign(sissWayInfo)
            .then((signedDocDataList: ISeSignResultData[]) => {
                this.isIntegrationWebService.onIsIntegrationActive();

                // Controllo che i documenti siano firmati - notifico quelli falliti - archivio i bytearray
                let partiallySigned = 0;
                forEach(signedDocDataList, (data: ISeSignResultData) => {
                    if (data.signed) {
                        // Marco come firmato, aggiungo il bytearray del p7m e rimuovo il bytearray del pdf
                        for (let n = 0; n < sissWayInfo.docDataArray.length; n++) {
                            if (sissWayInfo.docDataArray[n].reportId == data.reportId) {
                                sissWayInfo.docDataArray[n].signed = true;
                                sissWayInfo.docDataArray[n].signedDocByteArray = data.signedDocByteArray; // Mappo i p7m di ritorno
                                sissWayInfo.docDataArray[n].srcDocByteArray = undefined; // rimuovo il byte array del doc da firmare
                                break;
                            }
                        }
                    } else {
                        partiallySigned++;
                        // Marco come NON firmato e rimuovo il bytearray del pdf
                        for (let n = 0; n < sissWayInfo.docDataArray.length; n++) {
                            if (sissWayInfo.docDataArray[n].reportId == data.reportId) {
                                sissWayInfo.docDataArray[n].signed = false;
                                sissWayInfo.docDataArray[n].signedDocByteArray = undefined;
                                sissWayInfo.docDataArray[n].srcDocByteArray = undefined;
                                sissWayInfo.docDataArray[n].serverError =
                                    data.serverError !== null && data.serverError !== ''
                                        ? this.translateService.instant(data.serverError || '')
                                        : this.translateService.instant('SignReportError');
                                sissWayInfo.docDataArray[n].serverErrorCode =
                                    data.serverErrorCode !== null && data.serverErrorCode !== ''
                                        ? this.translateService.instant(data.serverErrorCode || '')
                                        : '';
                                sissWayInfo.docDataArray[n].serverErrorDetail =
                                    data.serverErrorDetail !== null && data.serverErrorDetail !== ''
                                        ? this.translateService.instant(data.serverErrorDetail || '')
                                        : this.translateService.instant('Exception_GenericFault');
                                break;
                            }
                        }

                        // Notifica errore
                        let reportInfo = this.reportErrorDetailList.find((item) => item.id == data.reportId);
                        let reportDescr = reportInfo && reportInfo.message ? reportInfo.message : data.reportId;

                        if (!data.serverError) data.serverError = this.errorMessageKey;

                        let errMsg =
                            this.translateService.instant('Report') +
                            ': ' +
                            reportDescr +
                            ' - ' +
                            this.translateService.instant(data.serverError, { p0: '' });
                        errMsg += data.serverErrorDetail ? ': ' + data.serverErrorDetail : '';

                        this.notificationService.error(
                            errMsg,
                            this.translateService.instant('PartiallySignedDoc', {
                                p0: partiallySigned,
                                p1: signedDocDataList.length,
                            }),
                        );
                    }
                });

                // Tutti non firmati o firmati parzialmente mi fermo - Da gestire il caso parziale di firma
                if (partiallySigned == signedDocDataList.length)
                    return Promise.reject({
                        message: this.translateService.instant('PartiallySignedDocOnTotal', {
                            p0: partiallySigned,
                            p1: signedDocDataList.length,
                        }),
                    });

                // Tutti o parzialmente firmati vado avati lo stesso
                const info: SeSignInfo = new SeSignInfo();
                info.type = SignType.SISSWay;
                info.sissWay = sissWayInfo;

                return Promise.resolve(info);
            })
            .catch((e) => {
                this.isIntegrationWebService.onIsIntegrationNotActive(null);

                if (!!e && e.status == -1)
                    return Promise.reject({ message: this.translateService.instant('IsIntegrationDisabled') });

                // Se il messaggio è un genericSignErrorMessage non notifico (verrà fatto in seguito...)
                if (!!e && !!e.message && e.message != this.genericSignErrorMessage)
                    return Promise.reject({ message: e.message });

                return Promise.reject({ message: this.genericSignErrorMessage });
            });
    }

    private signWithComped(compedInfo: SeCompedSignInfo): Promise<SeSignInfo> {
        return this.isIntegrationWebService
            .compedSignEx(compedInfo)
            .then((signedDocDataList: ISeSignResultData[]) => {
                // Check valori di ritorno per errori macro tipo credenziali - smart non inserita - smart non del firmatario
                if (!signedDocDataList || signedDocDataList.length < 1)
                    return Promise.reject({
                        message: this.translateService.instant('DigitalSign_InvalidCompedResponse'),
                    });
                else if (signedDocDataList[0].serverErrorCode == 'ErrorDigitallySignerNotAuthorized') {
                    /* FIRMATARIO NON COERENTE CON IL REFERTATORE */
                    this.errorMessageKey = 'DigitalSign_SignerNotAuthorized'; // <-- Messaggio in finestra di autenticazione e titolo della notifica
                    return Promise.reject({
                        message: this.translateService.instant(signedDocDataList[0].serverError || '', { p0: '' }),
                    }); // <-- dettaglio della notifica
                } else if (signedDocDataList[0].serverErrorCode == 'ErrorNotValidPassword') {
                    /* CREDENZIALI ERRATE */
                    this.errorMessageKey = 'Exception_CredentialsFault'; // <-- Messaggio in finestra di autenticazione e titolo della notifica
                    return Promise.reject({
                        message: this.translateService.instant(signedDocDataList[0].serverError || '', { p0: '' }),
                    }); // <-- dettaglio della notifica
                } else if (signedDocDataList[0].serverErrorCode == 'ErrorSmartCardNotIntoTheReader') {
                    /* SMART NON INSERITA */
                    this.errorMessageKey = 'DigitalSign_SmartCardNotPresent'; // <-- Messaggio in finestra di autenticazione e titolo della notifica
                    return Promise.reject({
                        message: this.translateService.instant(signedDocDataList[0].serverError || '', { p0: '' }),
                    }); // <-- dettaglio della notifica
                } else if (signedDocDataList[0].serverErrorCode == 'ErrorSmartCardReaderGenericError') {
                    /* SMART NON INSERITA */
                    this.errorMessageKey = 'DigitalSign_SmartCardReaderGenericError'; // <-- Messaggio in finestra di autenticazione e titolo della notifica
                    return Promise.reject({
                        message: this.translateService.instant(signedDocDataList[0].serverError || '', { p0: '' }),
                    }); // <-- dettaglio della notifica
                } else if (signedDocDataList[0].serverErrorCode == 'ErrorSmartCardGenericError') {
                    /* SMART NON INSERITA */
                    this.errorMessageKey = 'DigitalSign_SmartCardGenericError'; // <-- Messaggio in finestra di autenticazione e titolo della notifica
                    return Promise.reject({
                        message: this.translateService.instant(signedDocDataList[0].serverError || '', { p0: '' }),
                    }); // <-- dettaglio della notifica
                }

                this.isIntegrationWebService.onIsIntegrationActive();

                // Controllo che i documenti siano firmati - notifico quelli falliti - archivio i bytearray
                let partiallySigned = 0;
                forEach(signedDocDataList, (data: ISeSignResultData) => {
                    if (data.signed) {
                        // Marco come firmato, aggiungo il bytearray del p7m e rimuovo il bytearray del pdf
                        for (let dataArray of compedInfo.docDataArray) {
                            if (dataArray.reportId == data.reportId) {
                                dataArray.signed = true;
                                dataArray.signedDocByteArray = data.signedDocByteArray; // Mappo i p7m di ritorno
                                dataArray.srcDocByteArray = undefined; // rimuovo il byte array del doc da firmare
                                break;
                            }
                        }
                    } else {
                        partiallySigned++;

                        // Marco come NON firmato e rimuovo il bytearray del pdf
                        for (let dataArray of compedInfo.docDataArray) {
                            if (dataArray.reportId == data.reportId) {
                                dataArray.signed = false;
                                dataArray.signedDocByteArray = undefined;
                                dataArray.srcDocByteArray = undefined;
                                break;
                            }
                        }

                        // Notifica errore
                        let reportInfo = this.reportErrorDetailList.find(
                            (item: { id: string }) => item.id == data.reportId,
                        );
                        let reportDescr = reportInfo && reportInfo.message ? reportInfo.message : data.reportId;

                        let errMsg =
                            this.translateService.instant('Report') +
                            ': ' +
                            reportDescr +
                            ' - ' +
                            this.translateService.instant(data.serverError || '', { p0: '' });
                        errMsg += data.serverErrorDetail ? ': ' + data.serverErrorDetail : '';

                        this.notificationService.error(
                            errMsg,
                            this.translateService.instant('PartiallySignedDoc', {
                                p0: partiallySigned,
                                p1: signedDocDataList.length,
                            }),
                        );
                    }
                });

                // Tutti non firmati o firmati parzialmente mi fermo - Da gestire il caso parziale di firma
                if (partiallySigned == signedDocDataList.length)
                    return Promise.reject({
                        message: this.translateService.instant('PartiallySignedDocOnTotal', {
                            p0: partiallySigned,
                            p1: signedDocDataList.length,
                        }),
                    });

                // Tutti o parzialmente firmati vado avati lo stesso
                const info: SeSignInfo = new SeSignInfo();
                info.type = SignType.CompedDigitalSign;
                info.comped = compedInfo;

                return Promise.resolve(info);
            })
            .catch((e) => {
                this.isIntegrationWebService.onIsIntegrationNotActive(null);

                if (!!e && e.status == -1)
                    return Promise.reject({ message: this.translateService.instant('IsIntegrationDisabled') });

                // Se il messaggio è un genericSignErrorMessage non notifico (verrà fatto in seguito...)
                if (!!e && !!e.message && e.message != this.genericSignErrorMessage)
                    return Promise.reject({ message: e.message });

                return Promise.reject({ message: this.genericSignErrorMessage });
            });
    }

    private handleCDAErrorOnTotal(signedDocDataList: ISeSignResultData[]): string {
        let cdaErrorCount: number = 0;
        let message: string = '';
        forEach(signedDocDataList, (data) => {
            const cdaNotInjected = data.cdaFlag && data.cdaFlag.cdaInjected == false;
            const cdaNotValidated = data.cdaFlag && data.cdaFlag.cdaValidated == false;
            if (data.cdaFlag && (cdaNotInjected || cdaNotValidated)) {
                cdaErrorCount++;

                message =
                    this.translateService.instant('Report') +
                    ' [ID:  ' +
                    data.reportId +
                    ']: - ' +
                    this.translateService.instant(cdaNotInjected ? 'CDANotCreated' : 'RadioCDANotValidated');

                if (data.serverErrorDetail) message += ': ' + data.serverErrorDetail;

                this.notificationService.warning(message, this.translateService.instant('CDAWarning'));
            }
        });

        if (cdaErrorCount > 0) {
            message =
                cdaErrorCount == 1
                    ? this.translateService.instant('CDANotCreated')
                    : this.translateService.instant('PartiallyCDANotCreatedOnTotal', {
                          p0: cdaErrorCount,
                          p1: signedDocDataList.length,
                      });

            // Usiamo l'alert di sistema per bloccare le operazioni
            window.alert(message);
        }

        return message;
    }

    private handleSigificantImagesErrors(dataList: ISeSignResultData[]) {
        const dataInError = dataList.filter(
            (data) =>
                data.significantImagesInfo &&
                data.significantImagesInfo.significantImagesOnPacs >
                    data.significantImagesInfo.maxSignificantImagesToAdd,
        );
        if (!dataInError.length) return;
        const maxImages = dataInError[0]?.significantImagesInfo?.maxSignificantImagesToAdd ?? 0;
        let message = this.translateService.instant('significantImagesErrorHeader', {
            p0: maxImages,
        });
        message +=
            '\r\n' +
            dataInError
                .map((data) => {
                    const info = data?.reportDescr ?? data.reportId;
                    const images = data.significantImagesInfo?.significantImagesOnPacs ?? 0;
                    return this.translateService.instant('significantImagesErrorDetail', { p0: info, p1: images });
                })
                .join('\r\n');
        message += '\r\n' + this.translateService.instant('significantImagesErrorFooter');
        this.notificationService.error(message);
    }
}
